<!-- DocumentDetails.vue -->
<template>
  <div>
    <!-- Buttons: Generate Updated Spreadsheet-->
    <Level class="stk-pt-8">
      <LevelLeft class="action-buttons-container">
        <Buttons>
          <!-- TODO: UNCOMMENT BELOW WHEN WE WANT TO ADD VALIDATION BACK TO UI -->
          <o-tooltip
            variant="primary"
            position="right"
            :label="validationTooltip"
            multiline
          >
            <Button
              size="3_5"
              variant="primary"
              :disabled="!canReRunValidation"
              @click="reRunValidation"
            >
              Re-run Doc CheckUp
            </Button>
          </o-tooltip>

          <o-tooltip
            variant="primary"
            position="right"
            :label="spreadsheetTooltip"
            multiline
          >
            <Button
              size="3_5"
              @click="generateUpdatedSpreadsheet"
              iconBefore="fas fa-download"
            >
              <span>Download Spreadsheet</span>
            </Button>
          </o-tooltip>

          <Button
            size="3_5"
            variant="primary"
            :disabled="!hasUnsavedChanges"
            :loading="savingChanges"
            @click="saveAllChanges(documentId)"
            iconBefore="fas fa-save"
          >
            <span>Save All Changes</span>
          </Button>
        </Buttons>

          <Button
            size="2_5"
            iconOnly="fas fa-trash"
            @click="deleteDocument"
          />


        <div class="portfolio-button-separator">
          <Button
            size="2_5"
            variant="primary"
            iconBefore="fas fa-plus"
            @click="addToPortfolio"
          >
            <span>Add to Portfolio</span>
          </Button>
        </div>
      </LevelLeft>

      <LevelRight>
        <Buttons size="3_5">
          <span>PDF size</span>

          <Button
            :variant="pdfLayout === 'half' ? 'primary' : ''"
            @click="pdfLayout = 'half'"
          >
            Half
          </Button>

          <Button
            :variant="pdfLayout === 'fixed' ? 'primary' : ''"
            @click="pdfLayout = 'fixed'"
          >
            Fixed
          </Button>

          <Button
            :variant="pdfLayout === 'hidden' ? 'primary' : ''"
            @click="pdfLayout = 'hidden'"
          >
            Hidden
          </Button>
        </Buttons>
      </LevelRight>
    </Level>

    <div class="wrap" :class="`layout-${pdfLayout}`">
      <Stack v-if="extractInfo.length" gap="8" class="main stk-pt-8">
        <!-- Details Column -->
        <Stack gap="2">
          <!-- Regular Fields -->
          <div
            v-for="item in nonTableItems"
            :key="item.id"
            class="row"
          >
            <Label class="row-label">{{ item.descriptive_key }}</Label>

            <input
              v-model="item.value"
              :class="{'field-edited': pendingChanges[item.id]}"
              :maxlength="item.metadata.max_length"
              class="stk-input row-input"
              type="text"
              @input="trackFieldChange(item)"
            >

            <Button
              v-if="item.metadata?.page"
              iconOnly="fas fa-search"
              @click="showSourceLocation(item)"
            />

            <!-- <FieldSelector
              class="button is-primary"
              :fieldValue="item.value" 
              :descriptiveKey="item.descriptive_key"
              v-bind:callback="fieldsSelected"
            >
              Save to Dossier
            </FieldSelector> -->
          </div>
        </Stack>

        <!-- Tables -->
        <Stack
          v-for="(tablesByNumber, section) in groupedTables"
          :key="section"
          gap="0"
        >
          <div class="tw-flex tw-items-center tw-gap-4">
            <Title>
              {{ formatSectionTitle(section) }}
            </Title>

            <!-- <Button
              v-if="groupedTables[section]"
              @click="highlightTable(section, groupedTables[section].headers.concat(...groupedTables[section].rows.flat()))"
              iconOnly="fas fa-search"
              spacing="tight"
            /> -->
            <Button
              v-if="Object.keys(tablesByNumber).length"
              @click="highlightTable(
                section,
                Object.values(tablesByNumber)
                      .flatMap(tbl => [...tbl.headers, ...tbl.rows.flat()])
              )"
              iconOnly="fas fa-search"
              spacing="tight"
            />
          </div>
              <!-- now render each individual table -->
          <div
            v-for="(table, tableNumber) in tablesByNumber"
            :key="tableNumber"
            class="tw-mb-6"
          >

          <div class="table-container">
            <table class="table is-narrow is-fullwidth is-bordered">
              <thead class="has-background-white has-text-grey">
                <tr>
                  <th
                      v-for="(header, index) in table.headers"
                    :key="header?.id || `col-${index}`"
                    class="table-cell"
                    @mouseenter="header && showPreview(header)"
                    @mouseleave="hidePreview"
                  >
                  <small>{{ header?.value || header?.original_label || `Column ${index + 1}` }}</small>
                    <!-- Preview Container for Headers -->
                    <div
                      v-if="previewItem === header && header.metadata?.page"
                      class="source-preview"
                    >
                      <div class="preview-content">
                        <div
                          class="preview-highlight"
                          :style="{
                            left: `${getBoundingBoxValue(header.metadata.geometry.bounding_box, header.metadata.geometry.polygon, 'x', 'Left') * 100}%`,
                            top: `${getBoundingBoxValue(header.metadata.geometry.bounding_box,header.metadata.geometry.polygon, 'y', 'Top') * 100}%`,
                            width: `${getBoundingBoxValue(header.metadata.geometry.bounding_box, header.metadata.geometry.polygon, 'width', 'Width') * 100}%`,
                            height: `${getBoundingBoxValue(header.metadata.geometry.bounding_box,header.metadata.geometry.polygon, 'height', 'Height') * 100}%`
                          }"
                        />
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, rowIndex) in table.rows"
                  :key="rowIndex"
                >
                  <td
                    v-for="cell in row"
                    :key="cell?.id"
                    class="table-cell"
                    @mouseenter="cell && showPreview(cell)"
                    @mouseleave="hidePreview"
                  >
                    <input
                      v-if="cell"
                      v-model="cell.value"
                      :class="{'field-edited': pendingChanges[cell.id]}"
                      class="input is-small"
                      type="text"
                      @input="trackFieldChange(cell)"
                    >
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        </Stack>
      </Stack>

      <!-- PDF Viewer Column -->
      <div class="side stk-pt-8">
        <Stack v-if="currentPdfUrl">
          <!-- Page Navigation -->
          <div class="page-navigation">
            <Button
              :disabled="currentPdfPage <= 1"
              @click="currentPdfPage--"
              iconOnly="fas fa-chevron-left"
            />

            <input
              v-model.number="currentPdfPage"
              type="number"
              min="1"
              :max="totalPages"
              class="stk-input"
              style="width: 5em;"
            >

            <span
              v-if="totalPages"
            >of {{ totalPages }}</span>

            <Button
              :disabled="currentPdfPage >= totalPages"
              @click="currentPdfPage++"
              iconOnly="fas fa-chevron-right"
            />
          </div>

          <div class="pdf-viewer">
            <canvas ref="pdfCanvas" />
            <canvas
              ref="highlightCanvas"
              class="highlight-canvas"
            />
          </div>
        </Stack>
      </div>
    </div>
    <!-- <div v-else class="has-text-centered">
      <p>Processing document details...</p>
    </div> -->
  </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist';
import {
  Button,
  Buttons,
  Label,
  Level,
  LevelLeft,
  LevelRight,
  Stack,
  Title,
} from '@stakeui';

// Use webpack's publicPath which is correctly configured for both dev and prod
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs';
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(workerUrl, window.location.origin).href;
const pdfWorker = new pdfjsLib.PDFWorker();

import FieldSelector from './FieldSelector.vue';

export default {
  name: 'DocumentDetails',

  components: {
    // VuePdfEmbed,
    FieldSelector,
    Button,
    Buttons,
    Label,
    Level,
    LevelLeft,
    LevelRight,
    Stack,
    Title,
  },

  props: {
    extractInfo: {
      type: Array,
      required: true,
    },
    documentUrl: {
      type: String,
      default: null,
    },
    documentId: {
      type: String,
      default: null,
    },
    classification: {
      type: String,
      default: null,
    },
    filename: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      allowedValidationTypes: ['SEI Additional Investment Form Multiple SIMPLE IRA Accounts', 'SEI Additional Investment Form Multiple Qualified Plan Accounts'], // Specify document types that can re-run validation. Right now this is just SEI
      validationPassed: null, // Tracks validation status: true, false, or null for unknown
      activeTab: 'details',
      currentPdfUrl: null,
      currentPdfPage: 1,
      currentGeometry: null,
      totalPages: 0,
      pdfInstance: null,
      sourcePage: null,
      previewItem: null,
      pdfDoc: null,
      pdfPage: null,
      pdfScale: 1.5,
      pdfLayout: 'half',
      hidePreviewTimeout: null,
      hoveredItem: null,
      editedTables: {}, // Track which tables have been edited
      editedFields: {}, // Track edited non-table items
      pendingChanges: {}, // Track all changed fields: { fieldId: newValue }
      hasUnsavedChanges: false, // Flag to enable/disable save button
      savingChanges: false,
    };
  },

  computed: {
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
      };
    },

    // Separate table and non-table items
    nonTableItems() {
      return this.extractInfo.filter(item => 
        !item.metadata?.table?.is_table && // Exclude table cells
        item.type !== "table summary"      // Also exclude table summaries
      );
    },
    hasEdits() {
      const fieldEdits = Object.keys(this.editedFields).length > 0; // Check if any fields are edited
      const tableEdits = Object.keys(this.editedTables).length > 0; // Check if any tables are edited
      return fieldEdits || tableEdits;
    },
    spreadsheetTooltip() {
      return this.hasEdits ?
      'Click to download a new spreadsheet' :
      'After making edits, you can generate a new spreadsheet.';
    },

    canReRunValidation() {
      return true;
      // allowing rerun validation for all doc types now
      //return this.hasEdits;
      //return this.allowedValidationTypes.includes(this.classification) && this.hasEdits;
      // return this.hasEdits
    },
    validationTooltip() {
      console.log('document classification', this.classification);
      console.log('had edits?', this.hasEdits);
      if (!this.hasEdits) {
        return 'You can re-run Doc CheckUp after making edits';
      }
      return 'Click to re-run Doc CheckUp after making edits';
    },

    tableSummaries() {
      return this.extractInfo.filter(item => 
        item.type === "table summary" && 
        item.metadata?.table?.table_number !== undefined
      ).reduce((acc, item) => {
        acc[item.metadata.table.table_number] = item;
        return acc;
      }, {});
    },

    // Group table items by section and organize into table structure
    groupedTables() {
      // Section → (tableNumber → table-object)
      const tables = {};
      const tableItems = this.extractInfo.filter(item => item.metadata?.table?.is_table);
      const summaries = this.tableSummaries;
      const sectionMapping = new Map(); // tableNumber → section label
      const groupMap = new Map();       // tableNumber → { headers, maxColumns, sectionLabel, summary }

      // First pass: group by tableNumber and collect headers
      tableItems.forEach(item => {
        const { table_number: tableNumber, row, column } = item.metadata.table;
        const colIndex = column - 1;

        if (!groupMap.has(tableNumber)) {
          groupMap.set(tableNumber, {
            headers: [],
            maxColumns: 0,
            sectionLabel: null,
            summary: summaries[tableNumber] || null
          });
          sectionMapping.set(tableNumber, `Table ${tableNumber}`); // default label
        }

        const group = groupMap.get(tableNumber);
        group.maxColumns = Math.max(group.maxColumns, colIndex + 1);

        // header cells live in row 1
        if (row === 1) {
          group.headers[colIndex] = item;

          // use the first header cell’s `item.section` as the section label if present
          if (colIndex === 0) {
            const raw = item.section?.trim();
            sectionMapping.set(tableNumber, raw || `Table ${tableNumber}`);
          }
        }
      });

      // Assign final sectionLabel into each group
      groupMap.forEach((group, tableNumber) => {
        group.sectionLabel = sectionMapping.get(tableNumber);
      });

      // Second pass: initialize tables[section][tableNumber]
      groupMap.forEach((group, tableNumber) => {
        const section = group.sectionLabel;
        if (!tables[section]) {
          tables[section] = {};
        }
        tables[section][tableNumber] = {
          headers: [...group.headers],
          rows: [],                  // to be filled next
          maxColumns: group.maxColumns,
          summary:   group.summary
        };
        console.log(`Assigning table ${tableNumber} to section "${section}"`);
      });

      // Third pass: build body rows per tableNumber
      const tempRowMaps = {}; // tableNumber → Map<rowIndex, Array>

      tableItems
        .filter(item => item.metadata.table.row > 1)
        .forEach(item => {
          const { table_number: tableNumber, row, column } = item.metadata.table;
          const section = sectionMapping.get(tableNumber);
          const sectionTables = tables[section];
          if (!sectionTables || !sectionTables[tableNumber]) return;

          const maxCols = sectionTables[tableNumber].maxColumns;
          const rowIndex = row - 2;   // zero-based for body rows
          const colIndex = column - 1;

          if (!tempRowMaps[tableNumber]) {
            tempRowMaps[tableNumber] = new Map();
          }
          const rowMap = tempRowMaps[tableNumber];
          if (!rowMap.has(rowIndex)) {
            rowMap.set(rowIndex, new Array(maxCols).fill(null));
          }
          rowMap.get(rowIndex)[colIndex] = item;
        });

      // Attach the built rows back into tables
      Object.entries(tempRowMaps).forEach(([tn, rowMap]) => {
        const tableNumber = Number(tn);
        const section = sectionMapping.get(tableNumber);
        tables[section][tableNumber].rows = Array.from(rowMap.values());
      });

      return tables;
    }

  },

  watch: {
    documentUrl: {
      immediate: true,
      handler(newUrl) {
        if (newUrl) {
          this.currentPdfUrl = newUrl;
        }
      },
    },
    activeTab(newVal) {
      if (newVal === 'pdf' && this.documentUrl && !this.currentPdfUrl) {
        this.currentPdfUrl = this.documentUrl;
      }
    },
    currentPdfPage: {
      handler(newPage) {
        if (this.pdfDoc && newPage >= 1 && newPage <= this.totalPages) {
          this.renderPage(newPage);
        }
      },
    },

    currentPdfUrl: {
      immediate: true,
      async handler(newUrl) {
        if (newUrl) {
          try {
            this.pdfDoc = await pdfjsLib.getDocument({ url: newUrl, isEvalSupported: false, worker: pdfWorker }).promise;
            this.totalPages = this.pdfDoc.numPages;
            this.renderPage(this.currentPdfPage);
          } catch (error) {
            console.error('Error loading PDF:', error);
          }
        }
      },
    },
  },

  created() {
    // Load PDF URL when component is created
    if (this.documentUrl) {
      this.currentPdfUrl = this.documentUrl;
    }
  },

  beforeDestroy() {
    // Clean up any remaining timeout
    if (this.hidePreviewTimeout) {
      clearTimeout(this.hidePreviewTimeout);
    }
  },

  methods: {
    saveSelection() {
      console.log('Selected fields:', this.selectedFields);
      this.showModal = false;
    },
    fieldsSelected(selectedFields) {
      console.log("Selected fields:", selectedFields);
      // Handle the selected fields here (e.g., save to state or perform an action)
    },
    async saveToDossier(document_id, item) {
      console.log('save to dossier', document_id, item)
      // const payload = {
      //   name: item.descriptive_key,
      //   value: item.value,
      // };
      // const response = await fetch(`/api/v1/documents/${document_id}/save-to-dossier`, {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     ...this.headers, // Spread the headers computed property here
      //   },
      //   body: JSON.stringify(payload),
      // });
    },
    // Track changes (user edits) for any field (table or non-table)
    trackFieldChange(item) {
      if (!item || !item.id) return;
      
      // Store the current value in pendingChanges
      this.pendingChanges[item.id] = item.value;
      this.hasUnsavedChanges = true;
      console.log('Changes tracked:', this.hasUnsavedChanges, Object.keys(this.pendingChanges).length);
      // Continue to mark as edited for existing functionality
      if (item.metadata?.table?.is_table) {
        this.markTableAsEdited(item.metadata.table.table_number, item);
      } else {
        this.markFieldAsEdited(item);
      }
    },
    // Mark a table as edited when any cell in the table is changed
    markTableAsEdited(section, cell) {
      // Add this cell to the editedTables object for the given section
      if (!this.editedTables[section]) {
        this.editedTables[section] = [];
      }

      // Check if the cell is already in the list of edits
      const existingEdit = this.editedTables[section].find(edit => edit.id === cell.id);
      if (!existingEdit) {
        this.editedTables[section].push({
          id: cell.id,
          newValue: cell.value,
        });
        this.markFieldAsEdited(cell);
      } else {
        // Update the value if the edit already exists
        existingEdit.newValue = cell.value;
      }
    },

    // Save all pending user edits at once
    async saveAllChanges(documentId) {
      if (!Object.keys(this.pendingChanges).length) {
        return; // Nothing to save
      }
      this.savingChanges = true;
      try {
        const payload = {
          external_id: documentId,
          changes: this.pendingChanges
        };
        
        const response = await fetch(`/api/v1/documents/${documentId}/batch-edit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...this.headers,
          },
          body: JSON.stringify(payload),
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to save changes');
        }
        
        const result = await response.json();
        console.log('Batch save response:', result);
        
        // Reset tracking variables
        this.pendingChanges = {};
        this.hasUnsavedChanges = false;
        
        // Provide feedback to the user-- commenting out for now i think it's annoying
        //alert('All changes saved successfully');
      } catch (error) {
        //alert user if there was an error saving
        alert(`Error saving changes: ${error.message}`);
        console.error('Error saving batch changes:', error);
      }
      finally {
        this.savingChanges = false;
      }
    },
    markFieldAsEdited(item) {
      this.$set(this.editedFields, item.id, item.value);
    },


    computeTableBoundingBox(tableItems) {
      const pdfpage = this.pdfPage
      const boundingBoxes = tableItems.map(item => 
        item.metadata.geometry.bounding_box ||
        this.extractBoundingBoxFromPolygon(item.metadata.geometry.polygon, pdfpage, this.pdfScale)
      );

      const xMin = Math.min(...boundingBoxes.map(box => box.x));
      const yMin = Math.min(...boundingBoxes.map(box => box.y));
      const xMax = Math.max(...boundingBoxes.map(box => box.x + box.width));
      const yMax = Math.max(...boundingBoxes.map(box => box.y + box.height));

      return {
        x: xMin,
        y: yMin,
        width: xMax - xMin,
        height: yMax - yMin,
      };
    },

    // extractBoundingBoxFromPolygon(polygon, pdfPage) {
    //   if (!polygon || polygon.length === 0 || !pdfPage) return null;
      
    //   // Get the viewport at scale 1.0
    //   const viewport = pdfPage.getViewport({ scale: 1.0 });
      
    //   // Extract min/max values directly from the polygon points
    //   const xMin = Math.min(...polygon.map(p => p.X));
    //   const yMin = Math.min(...polygon.map(p => p.Y));
    //   const xMax = Math.max(...polygon.map(p => p.X));
    //   const yMax = Math.max(...polygon.map(p => p.Y));
      
    //   // Convert to normalized coordinates (0-1)
    //   // Since Azure gives coordinates in inches, and viewport is in points,
    //   // multiply by 72 (points per inch)
    //   const pointMultiplier = 72;
      
    //   return {
    //     x: (xMin * pointMultiplier) / viewport.width,
    //     y: (yMin * pointMultiplier) / viewport.height,
    //     width: ((xMax - xMin) * pointMultiplier) / viewport.width,
    //     height: ((yMax - yMin) * pointMultiplier) / viewport.height
    //   };
    // },

    extractBoundingBoxFromPolygon(polygon, pdfPage) {
      if (!polygon || polygon.length === 0 || !pdfPage) return null;
      
      // Get the viewport at scale 1.0
      const viewport = pdfPage.getViewport({ scale: 1.0 });
      
      // Extract min/max values directly from the polygon points
      const xMin = Math.min(...polygon.map(p => p.X));
      const yMin = Math.min(...polygon.map(p => p.Y));
      const xMax = Math.max(...polygon.map(p => p.X));
      const yMax = Math.max(...polygon.map(p => p.Y));
      
      // Convert to normalized coordinates (0-1)
      // Since Azure gives coordinates in inches, and viewport is in points,
      // multiply by 72 (points per inch)
      // NOTE ON AZURE GEOMETRY DATA-- RELEVANT IF WE START ACCEPTING IMAGES ####
      // https://learn.microsoft.com/en-us/python/api/azure-ai-formrecognizer/azure.ai.formrecognizer.boundingregion?view=azure-python#azure-ai-formrecognizer-boundingregion-polygon
      // A list of points representing the bounding polygon that outlines the document component. The points are listed in clockwise order relative to the document component orientation starting from the top-left. Units are in pixels for images and inches for PDF.
      const pointMultiplier = 72;
      
      return {
        x: (xMin * pointMultiplier) / viewport.width,
        y: (yMin * pointMultiplier) / viewport.height,
        width: ((xMax - xMin) * pointMultiplier) / viewport.width,
        height: ((yMax - yMin) * pointMultiplier) / viewport.height
      };
    },
    async highlightTable(section, tableItems) {
      if (!tableItems.length) return;

      // Get the table number from the first item
      const tableNumber = tableItems[0].metadata.table.table_number;
      
      // Check if we have a summary with a bounding box
      const summary = this.tableSummaries[tableNumber];
      
      if (summary && summary.metadata.geometry.bounding_box) {
        // Use the summary's bounding box directly for highlighting
        console.log('Using table summary geometry')
        const page = summary.metadata.page;
        this.showSourceLocation({ 
          metadata: { 
            page, 
            geometry: { 
              bounding_box: summary.metadata.geometry.bounding_box 
            } 
          } 
        });
      } else {
        // Fall back to computing bounding box from all cells
        console.log('Highlighting table from all table cells')
        const boundingBox = this.computeTableBoundingBox(tableItems);
        const page = tableItems[0].metadata.page;
        this.showSourceLocation({ 
          metadata: { 
            page, 
            geometry: { 
              bounding_box: boundingBox 
            } 
          } 
        });
      }
    },
    // generate a spreadsheet of the extracted info
    async generateUpdatedSpreadsheet() {
      try {
        const response = await fetch(`/api/v1/process/document_xls/?document_id=${this.documentId}`, {
          headers: this.headers,
        });
        const data = await response.json();
        if (data.url) {
          const link = document.createElement('a');
          link.href = data.url;
          link.download = data.filename || 'updated_document.xlsx'; // Default filename
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          console.error('No URL found in the response');
          alert('Failed to generate spreadsheet: No URL found in the response.');
        }
      } catch (error) {
        console.error('Error generating updated spreadsheet:', error);
        alert('Failed to generate spreadsheet. Please try again.');
      }
    },

    async reRunValidation() {
      try {
        // Make the POST request to the backend
        const response = await fetch(`/api/v1/documents/${this.documentId}/revalidate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...this.headers, // Spread the headers computed property here
          },
        });

        // Check if the response is OK
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to re-run validation checks');
        }

        // Parse and process the response
        const result = await response.json();
        console.log('Validation Result:', result);

        // Update the validation status
        if (Array.isArray(result.validation)) {
          const allChecksPassed = result.validation.every(v => v.passed);
          this.validationPassed = allChecksPassed; // Update UI status
        } else {
          this.validationPassed = false; // Handle unexpected validation response
        }
        // Emit an event to inform parent (DocumentView.vue)
        this.$emit('validation-updated');
      } catch (error) {
        console.error('Error re-running validation:', error.message);
        alert('Failed to re-run validation checks.');
      }
    },

    async showSourceLocation(item) {
      if (!(item.metadata?.geometry?.bounding_box || item.metadata?.geometry?.polygon)) {
          console.log('No geometry data found for item:', item);
          return;
        }
      

      if (item.metadata?.page && (item.metadata?.geometry?.bounding_box || item.metadata?.geometry?.polygon)) {
        console.log('Showing source location for item:', item);
        this.hoveredItem = item;
        this.currentPdfPage = Number(item.metadata.page);

        // Wait for page render to complete (which will draw highlight via renderPage)
        await this.$nextTick();
        await this.renderPage(this.currentPdfPage);

        // Scroll the highlighted area into view
        const pdfpage = this.pdfPage
        const pdfscale = this.pdfScale
        const viewport = this.pdfPage.getViewport({ scale: pdfscale });
        const canvas = this.$refs.pdfCanvas;

        // get geometry
        let box = item.metadata.geometry.bounding_box || null;
        // If bounding_box is missing, compute one from polygon
        if (!box && item.metadata.geometry.polygon) {
          box = this.extractBoundingBoxFromPolygon(item.metadata.geometry.polygon, pdfpage, pdfscale);
        }
        if (!box) {
          console.warn("No valid bounding box or polygon found.");
          return;
        }

        const scaledY = (1 - box.y - box.height) * viewport.height;
        canvas.parentElement.scrollTo({
          top: scaledY - canvas.parentElement.clientHeight / 2,
          behavior: 'smooth',
        });
      }
    },

    getBoundingBoxValue(box, polygon, primaryProperty, fallbackProperty) {
      if (box) {
        return box[primaryProperty] ?? box[fallbackProperty] ?? 0;
      }

      if (polygon) {
        // Extract min/max values from polygon coordinates
        const xMin = Math.min(...polygon.map(p => p.X));
        const yMin = Math.min(...polygon.map(p => p.Y));
        const xMax = Math.max(...polygon.map(p => p.X));
        const yMax = Math.max(...polygon.map(p => p.Y));

        switch (primaryProperty) {
          case 'x': return xMin;
          case 'y': return yMin;
          case 'width': return xMax - xMin;
          case 'height': return yMax - yMin;
        }
      }

      return 0;
    },


    async renderPage(pageNumber) {
      if (!this.pdfDoc) return;

      try {
        this.pdfPage = await this.pdfDoc.getPage(pageNumber);
        const pdfCanvas = this.$refs.pdfCanvas;
        const highlightCanvas = this.$refs.highlightCanvas;
        const pdfContext = pdfCanvas.getContext('2d');
        const highlightContext = highlightCanvas.getContext('2d');
        const viewport = this.pdfPage.getViewport({ scale: this.pdfScale });

        // Set both canvases to the same dimensions
        pdfCanvas.height = viewport.height;
        pdfCanvas.width = viewport.width;
        highlightCanvas.height = viewport.height;
        highlightCanvas.width = viewport.width;

        // Clear the highlight canvas
        highlightContext.clearRect(0, 0, highlightCanvas.width, highlightCanvas.height);

        // Render PDF
        await this.pdfPage.render({
          canvasContext: pdfContext,
          viewport: viewport,
        }).promise;

        // Draw highlight if we have a hovered item on this page
        if (this.hoveredItem && this.hoveredItem.metadata.page == pageNumber) {
          this.drawHighlight(this.hoveredItem.metadata.geometry, viewport, highlightContext);
        } else {
          console.log('No hovered item on this page');
        }
      } catch (error) {
        console.error('Error rendering PDF page:', error);
      }
    },
    drawHighlight(geometry, viewport, ctx) {
      if (!geometry) return;

      let x, y, width, height;

      if (geometry.bounding_box) {
        x = geometry.bounding_box.x ?? geometry.bounding_box.Left ?? 0;
        y = geometry.bounding_box.y ?? geometry.bounding_box.Top ?? 0;
        width = geometry.bounding_box.width ?? geometry.bounding_box.Width ?? 0;
        height = geometry.bounding_box.height ?? geometry.bounding_box.Height ?? 0;
      } else if (geometry.polygon && geometry.polygon.length > 0) {
        console.log("Using Polygon to Draw Highlight");
        const box = this.extractBoundingBoxFromPolygon(geometry.polygon, this.pdfPage, this.pdfScale);

        if (!box) {
          console.warn("Failed to compute bounding box from polygon");
          return;
        }

        x = box.x;
        y = box.y;
        width = box.width;
        height = box.height;
      } else {
        console.warn("No bounding data found for highlight.");
        return;
      }

      console.log("Drawing Highlight at:", { x, y, width, height });

      // Convert normalized (0-1) values to pixel dimensions
      const scaledX = x * viewport.width;
      const scaledY = y * viewport.height;
      const scaledWidth = width * viewport.width;
      const scaledHeight = height * viewport.height;

      console.log("Scaled Highlight:", { scaledX, scaledY, scaledWidth, scaledHeight });

      // Clear previous highlights
      ctx.clearRect(0, 0, viewport.width, viewport.height);

      // Draw highlight (filled and outlined)
      ctx.fillStyle = 'rgba(255, 255, 0, 0.3)';
      ctx.strokeStyle = '#ffd700';
      ctx.lineWidth = 2;
      ctx.fillRect(scaledX, scaledY, scaledWidth, scaledHeight);
      ctx.strokeRect(scaledX, scaledY, scaledWidth, scaledHeight);
    },

    // drawHighlight(geometry, viewport, ctx) {
    //   if (!geometry) return;

    //   // Check for bounding box
    //   if (geometry.bounding_box) {
    //     const x = geometry.bounding_box.x ?? geometry.bounding_box.Left ?? 0;
    //     const y = geometry.bounding_box.y ?? geometry.bounding_box.Top ?? 0;
    //     const width = geometry.bounding_box.width ?? geometry.bounding_box.Width ?? 0;
    //     const height = geometry.bounding_box.height ?? geometry.bounding_box.Height ?? 0;

    //     const scaledX = x * viewport.width;
    //     const scaledY = y * viewport.height;
    //     const scaledWidth = width * viewport.width;
    //     const scaledHeight = height * viewport.height;

    //     ctx.fillStyle = 'rgba(255, 255, 0, 0.3)';
    //     ctx.strokeStyle = '#ffd700';
    //     ctx.lineWidth = 2;
    //     ctx.fillRect(scaledX, scaledY, scaledWidth, scaledHeight);
    //     ctx.strokeRect(scaledX, scaledY, scaledWidth, scaledHeight);
    //     return;
    //   }

    //   // If bounding box is missing, check for polygon
    //   if (geometry.polygon && geometry.polygon.length > 0) {
    //     ctx.fillStyle = 'rgba(255, 255, 0, 0.3)';
    //     ctx.strokeStyle = '#ffd700';
    //     ctx.lineWidth = 2;

    //     ctx.beginPath();
    //     geometry.polygon.forEach((point, index) => {
    //       const scaledX = point.X * viewport.width;
    //       const scaledY = point.Y * viewport.height;
    //       if (index === 0) {
    //         ctx.moveTo(scaledX, scaledY);
    //       } else {
    //         ctx.lineTo(scaledX, scaledY);
    //       }
    //     });

    //     ctx.closePath();
    //     ctx.fill();
    //     ctx.stroke();
    //   }
    // },


    async showPreview(item) {
      if (item.metadata?.page && item.metadata?.geometry?.bounding_box) {
        this.previewItem = item;

        try {
          if (!this.pdfDoc) {
            this.pdfDoc = await pdfjsLib.getDocument({ url: this.documentUrl, isEvalSupported: false, worker: pdfWorker }).promise;
          }

          const page = await this.pdfDoc.getPage(Number(item.metadata.page));

          // Calculate scale to fit preview width
          const desiredWidth = 300;
          const viewport = page.getViewport({ scale: 1.0 });
          const scale = desiredWidth / viewport.width;
          const scaledViewport = page.getViewport({ scale });

          await this.$nextTick();
          const canvas = this.$el.querySelector('.preview-content canvas');
          if (!canvas) return;

          const ctx = canvas.getContext('2d');
          canvas.width = scaledViewport.width;
          canvas.height = scaledViewport.height;

          // Render PDF page
          await page.render({
            canvasContext: ctx,
            viewport: scaledViewport,
          }).promise;

          // Draw highlight
          this.drawHighlight(item.metadata.geometry, scaledViewport, ctx);
        } catch (error) {
          console.error('Error showing preview:', error);
        }
      }
    },

    hidePreview() {
      this.previewItem = null;
      if (this.hidePreviewTimeout) {
        clearTimeout(this.hidePreviewTimeout);
        this.hidePreviewTimeout = null;
      }
    },

    formatSectionTitle(section) {
      // Convert section name to title case and replace underscores
      return section
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
    },

    handlePreviewLeave() {
      this.hidePreviewTimeout = setTimeout(() => {
        this.hidePreview();
      }, 100);
    },

    cancelHidePreview() {
      if (this.hidePreviewTimeout) {
        clearTimeout(this.hidePreviewTimeout);
        this.hidePreviewTimeout = null;
      }
    },

    handlePreviewClick(item) {
      this.showSourceLocation(item);
      this.hidePreview();
    },

    addToPortfolio() {
      // TODO: Implement add to portfolio functionality
      console.log('Add to portfolio clicked for document:', this.documentId);
      this.$emit('add-to-portfolio', this.documentId);
    },

    deleteDocument() {
      // TODO: Implement delete functionality
      if (confirm('Are you sure you want to delete this document?')) {
        console.log('Delete clicked for document:', this.documentId);
        this.$emit('delete-document', this.documentId);
      }
    },
  },
};
</script>

<style scoped>
.wrap {
  align-items: start;
  display: grid;
  gap: 2.5rem;
  position: relative;
}

.layout-half {
  grid-template-columns: 1fr 1fr;
}

.layout-fixed {
  grid-template-columns: 1fr 30rem;
}

.layout-hidden {
  grid-template-columns: 1fr;

  .side {
    display: none;
  }
}

.main {
  overflow: hidden;
}

.side {
  position: sticky;
  top: 0;
}

.pdf-viewer {
  aspect-ratio: 918 / 1188;
  position: relative;
  width: 100%;
  box-shadow: 0 0 0 1px var(--stk-border);
}

.pdf-viewer canvas {
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
}

.highlight {
  position: absolute;
  background-color: rgba(255, 255, 0, 0.3);
  border: 2px solid #ffd700;
  transition: all 0.2s ease;
  pointer-events: none;
}

.page-navigation {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1em;
}

.tabs {
  margin-bottom: 1.5rem;
}

.tabs ul {
  border-bottom-color: #dbdbdb;
}

.tabs li.is-active a {
  border-bottom-color: #485fc7;
  color: #485fc7;
}

.source-preview-container {
  position: relative;
  display: inline-block;
}

.source-preview {
  position: absolute;
  left: -320px;
  top: 0;
  transform: none;
  z-index: 1000;
  background: white;
  padding: 10px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  width: 320px;
  cursor: pointer; /* Add pointer cursor to indicate clickability */
}

.preview-content {
  position: relative;
  width: 300px;
  height: auto;
  overflow: hidden;
}

.preview-content canvas {
  width: 100%;
  height: auto;
  display: block;
}

.preview-highlight {
  position: absolute;
  pointer-events: none;
  background: rgba(255, 255, 0, 0.3);
  border: 2px solid #ffd700;
  z-index: 1;
  transform-origin: top left;
}

/* Remove the bridge element since we want the preview to disappear when not hovering */
.source-preview::before {
  content: '';
  position: absolute;
  top: 50%;
  left: auto;
  right: -8px;
  transform: translateY(-50%);
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-left: 8px solid white;
  border-right: none;
  filter: drop-shadow(0 -2px 2px rgba(0, 0, 0, 0.1));
}

.table-container {
  margin: 1rem 0;
  max-width: 100%;
  overflow-x: auto;
}

.table {
  background-color: inherit;
  font-size: 0.9em;
  width: 100%;
}

/* .table th,
.table td {
  padding: 0.5em 0.75em;
  vertical-align: middle;
  white-space: nowrap;
} */

.table th,
.table td {
  padding: 0.5em 0.75em;
  vertical-align: middle;
  white-space: normal; /* Allow text to wrap */
  overflow-wrap: break-word; /* Break words that are too long */
  word-break: break-word; /* Fallback for older browsers */
}

.subtitle {
  color: #363636;
  font-weight: 600;
}

/* .table-cell {
  position: relative;
} */

.source-preview {
  position: absolute;
  z-index: 100;
  background: white;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1);
  /* Position the preview to the right of the cell */
  left: 100%;
  top: 0;
}

.preview-content {
  position: relative;
  width: 300px;
}

.preview-highlight {
  position: absolute;
  background-color: rgba(255, 255, 0, 0.3);
  border: 2px solid rgba(255, 165, 0, 0.5);
  pointer-events: none;
}

/* Ensure table cells with previews don't get cut off */
.table-container {
  overflow: visible;
}

/* Add horizontal scroll for the table itself */
.table-container table {
  overflow-x: auto;
  display: block;
  max-width: 100%;
}

/* Ensure proper positioning context for preview */
.table th,
.table td {
  position: relative;
}

/* width for table cells */
.table-cell {
  position: relative;
  max-width: 500px; /* Set a reasonable width for headers */
  word-wrap: break-word; /* Break long words to fit within the width */
  white-space: normal; /* Allow text to wrap to the next line */
  text-align: center; /* Optional: Center-align text for better readability */
}

/* Add styles for the columns layout */
.pdf-display {
  height: calc(100vh - 150px); /* Adjust to fit viewport */
}

.pdf-display-pane {
  overflow-y: auto;
}

.highlight-canvas {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
}

.row {
  display: flex;
  align-items: center;
  gap: 0.5em;
}

.row-label {
  color: var(--stk-text-strong);
  width: 16em;
}

.row-input {
  flex-grow: 1;
}

.field-edited {
  border-color: #3273dc;
  outline: 1px solid #3273dc;
  background-color: rgba(50, 115, 220, 0.05);
}

.action-buttons-container {
  flex-direction: column;
  align-items: flex-start !important;
  gap: 0.75rem;
}

.portfolio-button-separator {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5em;
}
</style>
